import { create } from 'zustand'
import { z } from 'zod'
import type { User as SupabaseUser } from '@supabase/supabase-js'
import { supabaseClient, invalidateImpersonationTokenCache } from '~/shared/common/supabase/client'
import { fetchUserMe, submitUserUpdate } from '~/client/user/user.api'
import { userDetailsCollection } from '~/client/user/user.db'
import { UserSchema } from '~/shared/user/user.supabase.schema'
import type { User, UserUpdatePatch } from '~/shared/user/user.supabase.schema'
import { posthog, setImpersonationSuperProperties } from '~/client/common/posthog'
import { identifyCustomerIOClient } from '~/client/common/customerio'

// ─── Impersonation localStorage ───────────────────────────────────────────────

const IMPERSONATION_STORAGE_KEY = 'revelstreet_impersonation'

const StoredImpersonationSchema = z.object({
  token: z.string(),
  expiresAt: z.string(),
})

type StoredImpersonation = z.infer<typeof StoredImpersonationSchema>

function readStoredImpersonation(): StoredImpersonation | null {
  try {
    const raw = localStorage.getItem(IMPERSONATION_STORAGE_KEY)
    if (!raw) return null
    const parsed = StoredImpersonationSchema.safeParse(JSON.parse(raw))
    if (!parsed.success) {
      localStorage.removeItem(IMPERSONATION_STORAGE_KEY)
      return null
    }
    // Clear if expired.
    if (new Date(parsed.data.expiresAt).getTime() <= Date.now()) {
      localStorage.removeItem(IMPERSONATION_STORAGE_KEY)
      return null
    }
    return parsed.data
  } catch {
    localStorage.removeItem(IMPERSONATION_STORAGE_KEY)
    return null
  }
}

function writeStoredImpersonation(token: string, expiresAt: string) {
  try {
    const value: StoredImpersonation = { token, expiresAt }
    localStorage.setItem(IMPERSONATION_STORAGE_KEY, JSON.stringify(value))
  } catch {
    // Best-effort.
  }
}

function clearStoredImpersonation() {
  try {
    localStorage.removeItem(IMPERSONATION_STORAGE_KEY)
  } catch {
    // Best-effort.
  }
}

// ─── Zod schemas for /start and /status responses ─────────────────────────────

export const ImpersonationTargetSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  first_name: z.string(),
  last_name: z.string(),
})

export type ImpersonationTarget = z.infer<typeof ImpersonationTargetSchema>

const StatusImpersonationResponseSchema = z.object({
  active: z.boolean(),
  target_user: ImpersonationTargetSchema.optional(),
  // Full UserDetail shape — present when active is true and the server returned it.
  target_user_detail: z.record(z.string(), z.unknown()).optional(),
  expires_at: z.string().optional(),
})

// ─── PostHog identify guard ───────────────────────────────────────────────────
// INVARIANT: called with the admin's user + userDetail only.
// viewAsUser never reaches this function.

function identifyPostHogUser(user: SupabaseUser, userDetail: User) {
  // INVARIANT: always admin identity — viewAsUser never passed here.
  posthog.identify(user.id, {
    email: userDetail.email,
    name: [userDetail.first_name, userDetail.last_name].filter(Boolean).join(' ') || undefined,
    first_name: userDetail.first_name,
    last_name: userDetail.last_name,
    entity_name: userDetail.active_entity?.name,
    entity_type: userDetail.active_entity?.type,
    is_staff: userDetail.is_staff,
    is_superuser: userDetail.is_superuser,
  })
}

// ─── Auth state shape ─────────────────────────────────────────────────────────

type AuthState = {
  user: SupabaseUser | null
  userDetail: User | null
  // INVARIANT: viewAsUser/viewAsUserDetail are the ONLY fields set during impersonation.
  // userDetail stays the admin always.
  viewAsUser: ImpersonationTarget | null
  // Full UserDetail for the impersonated target. Populated from /start and /status
  // responses. Use useEffectiveUserDetail() to read the right identity for UI rendering.
  viewAsUserDetail: User | null
  impersonationToken: string | null
  impersonationExpiresAt: string | null
  initialized: boolean
  isLoading: boolean
  setUser: (user: SupabaseUser | null) => void
  setUserDetail: (userDetail: User | null) => void
  initializeAuth: () => Promise<void>
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  updateUserDetail: (changes: UserUpdatePatch) => Promise<void>
  refreshUserDetail: () => Promise<void>
  // Impersonation actions — NEVER mutate userDetail/user.
  startImpersonation: (params: { token: string; target_user: ImpersonationTarget; target_user_detail?: User; expires_at: string }) => void
  stopImpersonation: () => Promise<void>
  clearImpersonationLocal: () => void
}

let initializationPromise: Promise<void> | null = null
let refreshInterval: ReturnType<typeof setInterval> | null = null

// Hydrate localStorage at module load time (synchronous — before React renders).
const _storedImpersonation =
  typeof window !== 'undefined' ? readStoredImpersonation() : null

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  userDetail: null,
  viewAsUser: null,
  viewAsUserDetail: null,
  impersonationToken: _storedImpersonation?.token ?? null,
  impersonationExpiresAt: _storedImpersonation?.expiresAt ?? null,
  initialized: false,
  isLoading: false,

  setUser: (user) => set({ user }),
  setUserDetail: (userDetail) => set({ userDetail }),

  // ─── Impersonation actions ─────────────────────────────────────────────────
  // INVARIANT: these actions NEVER touch userDetail or user.

  startImpersonation: ({ token, target_user, target_user_detail, expires_at }) => {
    // Invalidate any cached target JWT from a previous session before activating the new one.
    invalidateImpersonationTokenCache()
    writeStoredImpersonation(token, expires_at)
    set({
      viewAsUser: target_user,
      viewAsUserDetail: target_user_detail ?? null,
      impersonationToken: token,
      impersonationExpiresAt: expires_at,
    })
    setImpersonationSuperProperties(target_user)
  },

  stopImpersonation: async () => {
    const token = get().impersonationToken
    if (token) {
      // Fetch the admin's current JWT so the /stop endpoint can validate admin identity.
      const { data: sessionData, error: sessionError } = await supabaseClient.auth.getSession()
      if (sessionError || !sessionData.session) {
        // Admin JWT is already gone — clear local state and treat as stopped.
        clearStoredImpersonation()
        set({
          viewAsUser: null,
          viewAsUserDetail: null,
          impersonationToken: null,
          impersonationExpiresAt: null,
        })
        setImpersonationSuperProperties(null)
        return
      }

      try {
        await fetch('/api/admin/impersonation/stop', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${sessionData.session.access_token}`,
            'X-Impersonate-Token': token,
          },
        })
      } catch {
        // Best-effort — always clear local state regardless.
      }
    }
    clearStoredImpersonation()
    invalidateImpersonationTokenCache()
    set({
      viewAsUser: null,
      viewAsUserDetail: null,
      impersonationToken: null,
      impersonationExpiresAt: null,
    })
    setImpersonationSuperProperties(null)
  },

  clearImpersonationLocal: () => {
    clearStoredImpersonation()
    invalidateImpersonationTokenCache()
    set({
      viewAsUser: null,
      viewAsUserDetail: null,
      impersonationToken: null,
      impersonationExpiresAt: null,
    })
    setImpersonationSuperProperties(null)
  },

  initializeAuth: async () => {
    if (initializationPromise) return initializationPromise

    initializationPromise = (async () => {
      set({ isLoading: true })
      try {
        const { data, error } = await supabaseClient.auth.getSession()
        if (error) throw error
        set({ user: data.session?.user ?? null })

        try {
          const arr = await userDetailsCollection.toArrayWhenReady()
          const supabaseId = data.session?.user?.id
          const existing = supabaseId
            ? arr.find((u) => u.username === supabaseId)
            : arr[0]
          set({ userDetail: existing ?? null })
        } catch {}

        userDetailsCollection.subscribeChanges(
          () => {
            const arr = Array.from(userDetailsCollection.values())
            const supabaseId = get().user?.id
            const next = supabaseId ? arr.find((u) => u.username === supabaseId) ?? null : arr[0] ?? null
            set({ userDetail: next })
          },
          { includeInitialState: true },
        )

        supabaseClient.auth.onAuthStateChange((_event, session) => {
          set({ user: session?.user ?? null })
          const arr = Array.from(userDetailsCollection.values())
          const supabaseIdNow = session?.user?.id
          const next = supabaseIdNow ? arr.find((u) => u.username === supabaseIdNow) ?? null : arr[0] ?? null
          set({ userDetail: next })
        })

        if (data.session?.user?.id) {
          get()
            .refreshUserDetail()
            .then(() => {
              const ud = get().userDetail
              if (data.session?.user && ud) {
                identifyPostHogUser(data.session.user, ud)
              }
              void identifyCustomerIOClient()
            })

          if (refreshInterval) clearInterval(refreshInterval)
          refreshInterval = setInterval(() => {
            get().refreshUserDetail()
          }, 5 * 60 * 1000)
        }
      } finally {
        set({ initialized: true, isLoading: false })
      }

      // If a token was restored from localStorage, verify it against the server
      // and hydrate viewAsUser. Run AFTER auth is marked initialized so the
      // admin JWT is available for the request.
      const restoredToken = get().impersonationToken
      if (restoredToken) {
        try {
          const { data: { session: adminSession }, error: sessionError } = await supabaseClient.auth.getSession()
          if (sessionError || !adminSession) {
            // Admin session is gone — impersonation can't be active. Wipe local state.
            get().clearImpersonationLocal()
          } else {
            const statusRes = await fetch('/api/admin/impersonation/status', {
              headers: {
                Authorization: `Bearer ${adminSession.access_token}`,
                'X-Impersonate-Token': restoredToken,
              },
            })
            if (statusRes.ok) {
              const body: unknown = await statusRes.json()
              const parsed = StatusImpersonationResponseSchema.safeParse(body)
              if (parsed.success && parsed.data.active && parsed.data.target_user && parsed.data.expires_at) {
                // Parse full UserDetail from target_user_detail if present.
                const targetUserDetail = parsed.data.target_user_detail
                  ? UserSchema.safeParse(parsed.data.target_user_detail)
                  : null
                set({
                  viewAsUser: parsed.data.target_user,
                  viewAsUserDetail: targetUserDetail?.success ? targetUserDetail.data : null,
                  impersonationExpiresAt: parsed.data.expires_at,
                })
                setImpersonationSuperProperties(parsed.data.target_user)
              } else {
                get().clearImpersonationLocal()
              }
            } else {
              get().clearImpersonationLocal()
            }
          }
        } catch {
          get().clearImpersonationLocal()
        }
      }
    })()

    await initializationPromise
  },

  login: async (email: string, password: string) => {
    const { data: supabaseAuthData, error: supabaseAuthError } = await supabaseClient.auth.signInWithPassword({
      email,
      password,
    })

    let authResponse

    if (!supabaseAuthError && supabaseAuthData?.user) {
      authResponse = supabaseAuthData
    } else {
      const functionUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/login`

      const response = await fetch(functionUrl, {
        method: 'POST',
        body: JSON.stringify({ email, password }),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
      })

      if (!response.ok) {
        const errorData = (await response.json().catch(() => ({}))) as { error?: string }
        throw new Error(errorData.error || 'The email or password you entered is invalid. Please try again')
      }

      const result = (await response.json()) as {
        supabase: { access_token: string; refresh_token: string }
      }

      const sessionResponse = await supabaseClient.auth.setSession(result.supabase)
      authResponse = sessionResponse.data
    }

    const userId = authResponse.user?.id ?? null
    if (!userId) {
      throw new Error('User not found')
    }

    const user = await fetchUserMe()
    if (!user) throw new Error('User not found')

    set({ user: authResponse.user ?? null, userDetail: user })

    if (authResponse.user) {
      identifyPostHogUser(authResponse.user, user)
    }
    void identifyCustomerIOClient()

    if (userDetailsCollection.has(user.id)) {
      const tx = userDetailsCollection.update(user.id, { optimistic: false }, (draft) => {
        Object.assign(draft, user)
      })
      await tx.isPersisted.promise
    } else {
      const tx = userDetailsCollection.insert(user, { optimistic: false })
      await tx.isPersisted.promise
    }

    if (refreshInterval) clearInterval(refreshInterval)
    refreshInterval = setInterval(() => {
      get().refreshUserDetail()
    }, 60 * 60 * 1000)
  },

  logout: async () => {
    if (refreshInterval) {
      clearInterval(refreshInterval)
      refreshInterval = null
    }

    // Revoke impersonation server-side before sign-out so the DB session
    // doesn't stay active past the admin's auth window (FR-4 / AS-7).
    if (get().impersonationToken !== null) {
      try {
        await get().stopImpersonation()
      } catch {
        // stopImpersonation failed (network down, JWT already expired).
        // Clear local state anyway so the store is clean after logout.
        get().clearImpersonationLocal()
      }
    }

    // scope: 'local' guarantees the local session is cleared even if the
    // current JWT is already expired (a 'global' signOut requires a valid
    // token to revoke server-side, and a 403 there leaves the local session
    // intact, which traps the user on /login -> / redirect loops).
    try {
      await supabaseClient.auth.signOut({ scope: 'local' })
    } catch {
      // ignore — we still hard-clear below
    }

    // Belt-and-braces: nuke any leftover supabase auth keys that signOut
    // may have skipped (e.g., when storage was written by a different SDK
    // version), so the next page load can't rehydrate a stale session.
    try {
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i)
        if (key && (key.startsWith('sb-') || key === 'supabase.auth.token')) {
          localStorage.removeItem(key)
        }
      }
    } catch {}

    posthog.reset()

    localStorage.removeItem('userDetails')

    const keys = Array.from(userDetailsCollection.keys())
    if (keys.length > 0) {
      const tx = userDetailsCollection.delete(keys, { optimistic: false })
      await tx.isPersisted.promise
    }
    set({ user: null, userDetail: null })
    window.location.replace('/login')
  },

  updateUserDetail: async (changes: UserUpdatePatch) => {
    // Route to viewAsUserDetail during impersonation so the admin's userDetail
    // is never mutated with the target's data. /api/user/update is impersonation
    // -aware server-side, so the DB write already targets the right user.
    const isImpersonating = get().viewAsUser !== null
    const current = isImpersonating ? get().viewAsUserDetail : get().userDetail
    if (!current) return
    const previous = JSON.parse(JSON.stringify(current)) as User
    const writeSlot = (next: User) =>
      isImpersonating ? set({ viewAsUserDetail: next }) : set({ userDetail: next })
    const optimisticTx = userDetailsCollection.update(current.id, (draft) => {
      Object.assign(draft, changes)
    })
    await optimisticTx.isPersisted.promise
    const updated = userDetailsCollection.get(current.id) ?? current
    writeSlot(updated)

    try {
      const fresh = await submitUserUpdate(changes)
      const persistTx = userDetailsCollection.update(current.id, { optimistic: false }, (draft) => {
        Object.assign(draft, fresh)
      })
      await persistTx.isPersisted.promise
      writeSlot(fresh)
      if (!isImpersonating) void identifyCustomerIOClient()
    } catch (error) {
      const rollbackTx = userDetailsCollection.update(current.id, { optimistic: false }, (draft) => {
        Object.assign(draft, previous)
      })
      await rollbackTx.isPersisted.promise
      writeSlot(previous)
      throw error
    }
  },

  refreshUserDetail: async () => {
    const userId = get().user?.id
    if (!userId) return

    try {
      const freshUserDetail = await fetchUserMe()
      if (!freshUserDetail) return

      if (userDetailsCollection.has(freshUserDetail.id)) {
        const tx = userDetailsCollection.update(freshUserDetail.id, { optimistic: false }, (draft) => {
          Object.assign(draft, freshUserDetail)
        })
        await tx.isPersisted.promise
      } else {
        const tx = userDetailsCollection.insert(freshUserDetail, { optimistic: false })
        await tx.isPersisted.promise
      }

      set({ userDetail: freshUserDetail })
    } catch (error) {
      console.error('Failed to refresh user detail:', error)
    }
  },
}))

if (typeof window !== 'undefined') {
  supabaseClient.auth.onAuthStateChange((event, session) => {
    if (event === 'SIGNED_IN') {
      const supabaseId = session?.user?.id
      const cached = supabaseId
        ? Array.from(userDetailsCollection.values()).find((u) => u.username === supabaseId) ?? null
        : null
      useAuthStore.setState({
        user: session?.user ?? null,
        userDetail: cached,
      })
      useAuthStore.getState().refreshUserDetail()

      if (refreshInterval) clearInterval(refreshInterval)
      refreshInterval = setInterval(() => {
        useAuthStore.getState().refreshUserDetail()
      }, 5 * 60 * 1000)
    } else if (event === 'SIGNED_OUT') {
      if (refreshInterval) {
        clearInterval(refreshInterval)
        refreshInterval = null
      }
      useAuthStore.setState({
        user: null,
        userDetail: null,
      })
    }
  })

  // Separate listener for impersonation cleanup. Kept apart from the main
  // auth listener so this concern can evolve (or be removed) without
  // touching the upstream auth handler shape.
  supabaseClient.auth.onAuthStateChange((event) => {
    if (event !== 'SIGNED_OUT') return
    // SIGNED_OUT can fire on cross-tab logout or token expiry. The admin's
    // JWT is already gone so we cannot call /stop server-side; clear local
    // impersonation state and the cached target JWT.
    // NOTE: we deliberately do NOT clear on SIGNED_IN — that event fires on
    // page reload after /start, and clearing there would wipe a freshly
    // established session. Stale state from a different admin is bounded by
    // the server-side admin_user_id check.
    useAuthStore.setState({
      viewAsUser: null,
      viewAsUserDetail: null,
      impersonationToken: null,
      impersonationExpiresAt: null,
    })
    clearStoredImpersonation()
    invalidateImpersonationTokenCache()
  })

  window.addEventListener('focus', () => {
    const { user } = useAuthStore.getState()
    if (user) {
      useAuthStore.getState().refreshUserDetail()
    }
  })
}

// ─── PostHog super-property subscription ─────────────────────────────────────
// Belt-and-suspenders: keep PostHog super-properties in sync with viewAsUser
// regardless of how viewAsUser changes (store mutations, devtools, etc.).
// The store actions (startImpersonation, stopImpersonation, clearImpersonationLocal)
// also call setImpersonationSuperProperties directly for immediate sync.

useAuthStore.subscribe((state, prevState) => {
  if (state.viewAsUser === prevState.viewAsUser) return
  // Lazy import to avoid circular dependency issue at module init time.
  import('~/client/common/posthog')
    .then(({ setImpersonationSuperProperties }) => {
      setImpersonationSuperProperties(state.viewAsUser)
    })
    .catch(() => {
      // Best-effort.
    })
})

// ─── Selectors ────────────────────────────────────────────────────────────────

/**
 * Returns the EFFECTIVE user identity for UI rendering.
 * - During impersonation: returns the target user's full UserDetail (viewAsUserDetail).
 * - Otherwise: returns the admin's userDetail.
 *
 * Use this for UI surfaces that should reflect "the user being viewed":
 * greeting, avatar, name, entity data, venue groups, profile cards.
 *
 * WARNING: do NOT use for permission gates (is_superuser, is_staff), analytics
 * identity, or audit actor. Use useAdminUserDetail() for those.
 */
export function useEffectiveUserDetail(): User | null {
  return useAuthStore((s) => s.viewAsUserDetail ?? s.userDetail)
}

/**
 * Returns the ADMIN (actor) user identity — always the logged-in admin.
 * - Always returns userDetail, regardless of impersonation state.
 *
 * Use this for permission gates (is_superuser, is_staff), admin-only UI controls,
 * audit/actor displays, analytics identity (PostHog distinct_id), and support tools.
 */
export function useAdminUserDetail(): User | null {
  return useAuthStore((s) => s.userDetail)
}
